import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs/operators';
import { ApiError, HttpService } from '../../../services/http.service';
import { requiredTrimmed } from '../../../utils/utils';
import { InputComponent } from '../../from-input/input.component';
import { CustomToastComponent } from '../../toast/custom-toast.component';

// Interface for dialog data
interface CategoryDialogData {
  initialData?: CategoryResponse;
  EditMode: boolean;
  type: boolean;
  // 0 = Student, 1 = Teacher, 2 = CSQ (only relevant when type = false / FAQ category mode)
  audienceType?: 0 | 1 | 2;
}

interface CategoryResponse {
  id: string;
  name: string;
  description: string;
  typeName: string;
  isActive: boolean | string;
}

interface ApiDataResponse {
  status: boolean;
  message: string;
  data: CategoryResponse | CategoryResponse[];
}

@Component({
  selector: 'app-add-category',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputComponent, MatDialogModule],
  templateUrl: './add-category.component.html',
  styleUrls: ['./add-category.component.scss'],
})
export class AddCategoryComponent implements OnInit, OnDestroy {
  @Output() formSubmitted = new EventEmitter<any>();

  form: FormGroup;
  isProcessing = false;
  isEditMode: boolean = false;
  eventCategoryMode: boolean = false;
  customErrorMessages = {
    name: { required: 'Please enter a category name.' },
    description: { required: 'Please enter a description.' },
    typeName: { required: 'Work Experience name is required.' },
  };

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AddCategoryComponent>,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: CategoryDialogData,
  ) {
    this.isEditMode = data?.EditMode || false;
    this.eventCategoryMode = data?.type || false;

    if (this.eventCategoryMode) {
      this.form = this.fb.group({
        typeName: ['', requiredTrimmed],
        description: ['', requiredTrimmed],
      });
    } else {
      this.form = this.fb.group({
        name: ['', requiredTrimmed],
      });
    }
  }

  ngOnInit(): void {
    if (this.isEditMode && this.data?.initialData) {
      this.patchFormForEdit(this.data.initialData);
    }
  }

  ngOnDestroy(): void {}

  patchFormForEdit(initialData: CategoryResponse): void {
    if (this.eventCategoryMode) {
      this.form.patchValue({
        typeName: initialData.typeName || '',
        description: initialData.description || '',
      });
    } else {
      this.form.patchValue({
        name: initialData.name || '',
      });
    }
  }

  saveCategory(formData: any): void {
    if (this.isProcessing) return;

    this.isProcessing = true;
    const payload: any = {
      categoryName: formData.name,
    };

    // Include audienceType for FAQ category creation/update when in FAQ mode
    if (!this.eventCategoryMode && this.data?.audienceType !== undefined) {
      payload['audienceType'] = this.data.audienceType;
    }
    const categoryPayload = {
      typeName: formData.typeName,
      description: formData.description,
    };
    const categoryID = this.data?.initialData?.id || '';

    const apiCall = this.isEditMode
      ? this.eventCategoryMode
        ? this.httpService.put<ApiDataResponse>(
            `settings/workexperiencetype/${categoryID}`,
            categoryPayload,
          )
        : this.httpService.put<ApiDataResponse>(
            `faqcategories/${categoryID}`,
            payload,
          )
      : this.eventCategoryMode
        ? this.httpService.post<ApiDataResponse>(
            'settings/workexperiencetype',
            categoryPayload,
          )
        : this.httpService.post<ApiDataResponse>('faqcategories', payload);

    apiCall
      .pipe(
        finalize(() => {
          this.isProcessing = false;
          this.form.enable();
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.showSuccessToast(response.message);
            this.formSubmitted.emit(formData);
            this.dialogRef.close({ success: true, data: response.data });
          }
        },
        error: (err: ApiError) => {
          this.showErrorToast(
            err?.message || 'Something went wrong. Please try again later.',
          );
        },
      });
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.saveCategory(this.form.value);
    } else {
      this.form.markAllAsTouched();
      this.scrollToFirstInvalidControl();
    }
  }

  onCancel(): void {
    if (this.isProcessing) return;
    this.dialogRef.close(false);
  }

  private scrollToFirstInvalidControl(): void {
    setTimeout(() => {
      const firstInvalidControl = document.querySelector(
        '.ng-invalid:not(form)',
      );
      if (firstInvalidControl) {
        firstInvalidControl.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        (firstInvalidControl as HTMLElement).focus();
      }
    }, 100);
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}
